import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Plus, Loader2, Copy, Trash2, Clock, CheckCircle2, XCircle, UserX, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS = { booked: { label: "Booked", c: "#06B6D4" }, completed: { label: "Completed", c: "#10B981" }, cancelled: { label: "Cancelled", c: "#94A3B8" }, no_show: { label: "No-show", c: "#F43F5E" }, rescheduled: { label: "Rescheduled", c: "#F59E0B" } };

export default function Calendars() {
  const { activeSubId } = useAuth();
  const [cals, setCals] = useState([]);
  const [appts, setAppts] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const load = useCallback(() => {
    if (!activeSubId) return;
    setLoading(true);
    Promise.all([api.get(`/calendars?sub_account_id=${activeSubId}`), api.get(`/appointments?sub_account_id=${activeSubId}`)])
      .then(([c, a]) => { setCals(c.data); setAppts(a.data); setSel((p) => c.data.find((x) => x.id === p?.id) || c.data[0] || null); setLoading(false); });
  }, [activeSubId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { const { data } = await api.post(`/calendars?sub_account_id=${activeSubId}`, { name }); toast.success("Calendar created"); setOpen(false); setName(""); await load(); setSel(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const save = async (patch) => {
    const { data } = await api.put(`/calendars/${sel.id}`, patch);
    setSel(data); setCals(cals.map((c) => (c.id === data.id ? { ...c, ...data } : c)));
  };
  const del = async (id) => { await api.delete(`/calendars/${id}`); toast.success("Deleted"); load(); };
  const copyLink = (id) => { navigator.clipboard?.writeText(`${window.location.origin}/b/${id}`); toast.success("Booking link copied"); };
  const setStatus = async (a, status) => { await api.put(`/appointments/${a.id}/status`, { status }); toast.success(`Marked ${STATUS[status].label}`); load(); };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const toggleDay = (i) => {
    const wd = sel.work_days || [];
    save({ work_days: wd.includes(i) ? wd.filter((x) => x !== i) : [...wd, i].sort() });
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Scheduling</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Calendars</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2 font-semibold" data-testid="new-calendar-button"><Plus className="w-4 h-4" />New calendar</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New calendar</DialogTitle></DialogHeader>
            <div className="space-y-1.5 py-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Strategy Call" data-testid="calendar-name-input" /></div>
            <DialogFooter><Button onClick={create} disabled={!name} data-testid="save-calendar-button">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="calendars">
        <TabsList>
          <TabsTrigger value="calendars" data-testid="tab-calendars">Calendars</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="tab-agenda">Appointments ({appts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="calendars" className="mt-6">
          {!sel ? <Card className="p-16 text-center text-muted-foreground bg-card border-border/70" data-testid="calendars-empty">No calendars yet. Create one to start taking bookings.</Card> : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                {cals.map((c) => (
                  <button key={c.id} onClick={() => setSel(c)} className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${sel.id === c.id ? "border-primary bg-primary/10" : "border-border/60 hover:bg-secondary/40"}`} data-testid={`calendar-item-${c.id}`}>
                    <p className="font-medium text-sm">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.upcoming} upcoming</p>
                  </button>
                ))}
              </div>
              <Card className="lg:col-span-2 p-6 bg-card border-border/70 space-y-5">
                <div className="flex items-center justify-between">
                  <Input value={sel.name} onChange={(e) => setSel({ ...sel, name: e.target.value })} onBlur={() => save({ name: sel.name })} className="text-lg font-display font-bold border-0 bg-transparent px-0 focus-visible:ring-0 w-auto" data-testid="calendar-title-input" />
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => copyLink(sel.id)} data-testid="copy-booking-link"><Copy className="w-4 h-4" />Link</Button>
                    <Button variant="outline" size="sm" onClick={() => del(sel.id)} className="text-destructive" data-testid="delete-calendar"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>

                <div><Label className="text-xs">Available days</Label>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {DAYS.map((d, i) => (
                      <button key={d} onClick={() => toggleDay(i)} className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${(sel.work_days || []).includes(i) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`} data-testid={`day-${i}`}>{d}</button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Start time"><Input type="time" defaultValue={sel.start_time} onBlur={(e) => save({ start_time: e.target.value })} data-testid="start-time" /></Field>
                  <Field label="End time"><Input type="time" defaultValue={sel.end_time} onBlur={(e) => save({ end_time: e.target.value })} data-testid="end-time" /></Field>
                  <Field label="Slot (min)"><Input type="number" defaultValue={sel.slot_minutes} onBlur={(e) => save({ slot_minutes: parseInt(e.target.value) || 30 })} data-testid="slot-min" /></Field>
                  <Field label="Buffer (min)"><Input type="number" defaultValue={sel.buffer_min} onBlur={(e) => save({ buffer_min: parseInt(e.target.value) || 0 })} data-testid="buffer-min" /></Field>
                  <Field label="Min notice (hrs)"><Input type="number" defaultValue={sel.min_notice_hours} onBlur={(e) => save({ min_notice_hours: parseInt(e.target.value) || 0 })} data-testid="min-notice" /></Field>
                  <Field label="Daily limit (0=∞)"><Input type="number" defaultValue={sel.daily_limit} onBlur={(e) => save({ daily_limit: parseInt(e.target.value) || 0 })} data-testid="daily-limit" /></Field>
                </div>

                <Field label="Booking type">
                  <Select value={sel.booking_type} onValueChange={(v) => save({ booking_type: v })}>
                    <SelectTrigger data-testid="booking-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single host</SelectItem>
                      <SelectItem value="round_robin">Round-robin (rotate team)</SelectItem>
                      <SelectItem value="collective">Collective (any team member)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>

                <div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Custom booking questions</Label>
                    <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => save({ custom_questions: [...(sel.custom_questions || []), { key: `q_${(sel.custom_questions || []).length + 1}`, label: "New question", required: false }] })} data-testid="add-question"><Plus className="w-3.5 h-3.5" />Add</Button>
                  </div>
                  <div className="space-y-2 mt-2">
                    {(sel.custom_questions || []).map((q, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input defaultValue={q.label} onBlur={(e) => save({ custom_questions: sel.custom_questions.map((x, k) => k === i ? { ...x, label: e.target.value } : x) })} className="h-8" data-testid={`question-${i}`} />
                        <Input defaultValue={q.key} onBlur={(e) => save({ custom_questions: sel.custom_questions.map((x, k) => k === i ? { ...x, key: e.target.value } : x) })} className="h-8 w-28 font-mono text-xs" />
                        <button onClick={() => save({ custom_questions: sel.custom_questions.filter((_, k) => k !== i) })} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Answers save to the contact. Booking tags the contact <span className="font-mono">{sel.tag_on_book}</span> and fires the automation engine. Times are UTC.</p>

                <div className="pt-4 border-t border-border/60">
                  <Label className="text-xs">Appointment reminders</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[["remind_24h", "24 hours before"], ["remind_1h", "1 hour before"]].map(([key, lbl]) => {
                      const on = sel[key] !== false;
                      return (
                        <button key={key} onClick={() => save({ [key]: !on })}
                          className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                          data-testid={`toggle-${key}`}>{on ? "✓ " : ""}{lbl}</button>
                      );
                    })}
                  </div>
                  <textarea
                    defaultValue={sel.reminder_message || ""}
                    onBlur={(e) => save({ reminder_message: e.target.value })}
                    rows={3}
                    className="mt-3 w-full rounded-lg bg-secondary/40 border border-border/60 p-3 text-sm outline-none focus:border-primary resize-none"
                    placeholder="Reminder message…"
                    data-testid="reminder-message" />
                  <p className="text-xs text-muted-foreground mt-1.5">Sent via email (+ Telegram if connected, SMS logged). Merge tags: <span className="font-mono">{"{{first_name}}"}</span>, <span className="font-mono">{"{{calendar}}"}</span>, <span className="font-mono">{"{{time}}"}</span>.</p>
                </div>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="agenda" className="mt-6">
          <Card className="bg-card border-border/70 divide-y divide-border/60">
            {appts.length === 0 && <div className="p-10 text-center text-muted-foreground" data-testid="agenda-empty">No appointments yet.</div>}
            {appts.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-testid={`appt-${a.id}`}>
                <CalendarClock className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{a.contact_name} · <span className="text-muted-foreground">{a.calendar_name}</span></p>
                  <p className="text-xs text-muted-foreground font-mono">{new Date(a.start).toLocaleString()} {a.assigned_to ? `· ${a.assigned_to.name}` : ""}</p>
                </div>
                <Badge variant="secondary" style={{ color: STATUS[a.status]?.c }}>{STATUS[a.status]?.label || a.status}</Badge>
                {a.status === "booked" && (
                  <div className="flex gap-1">
                    <button onClick={() => setStatus(a, "completed")} className="text-muted-foreground hover:text-emerald-400" title="Completed" data-testid={`complete-${a.id}`}><CheckCircle2 className="w-4 h-4" /></button>
                    <button onClick={() => setStatus(a, "no_show")} className="text-muted-foreground hover:text-rose-400" title="No-show" data-testid={`noshow-${a.id}`}><UserX className="w-4 h-4" /></button>
                    <button onClick={() => setStatus(a, "cancelled")} className="text-muted-foreground hover:text-foreground" title="Cancel" data-testid={`cancel-${a.id}`}><XCircle className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>; }
